// Selector de variables basado en el DICCIONARIO (labels legibles + tipos).
// Reemplaza al selector por columnas técnicas. Permite selección múltiple.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { OdkAPI } from "../api/endpoints";
import { AccionesSeleccion } from "./Layout";
import { varKind, type VarKind } from "../lib/varkind";

export interface VariablePickerProps {
  projectId: number | null;
  formId: string | null;
  selected: string[];               // names seleccionados
  onToggle: (name: string) => void;
  multiple?: boolean;
  filterTypes?: string[];           // p.ej. ["select_one"] para sólo categóricas
  /**
   * Solo las numéricas o solo las categóricas, según `varKind`. Si ninguna
   * variable del diccionario es de esa clase se enseñan todas y se dice: una
   * `edad` declarada como `text` en el XLSForm sigue siendo una edad.
   */
  filterKind?: Exclude<VarKind, "other">;
  placeholder?: string;
  /** Con esto aparecen «Todas / Ninguna». Recibe la lista completa ya resuelta. */
  onSetSelected?: (names: string[]) => void;
}

export default function VariablePicker(props: VariablePickerProps) {
  const { projectId, formId, selected, onToggle, filterTypes, filterKind, placeholder, onSetSelected } = props;
  const [q, setQ] = useState("");

  const dictQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  let entries = dictQ.data ?? [];
  let sinFiltroDeClase = false;
  if (filterKind) {
    // Las técnicas (SubmissionDate, instanceID…) no son nada que seguir en el tiempo.
    entries = entries.filter((v) => !v.is_technical);
    const deLaClase = entries.filter((v) => varKind(v) === filterKind);
    if (deLaClase.length) entries = deLaClase;
    else sinFiltroDeClase = entries.length > 0;
  }
  if (filterTypes && filterTypes.length) {
    entries = entries.filter((v) => {
      const t = (v.type || "").toLowerCase();
      return filterTypes.some((f) => t.startsWith(f.toLowerCase()));
    });
  }
  if (q.trim()) {
    const s = q.toLowerCase();
    entries = entries.filter(
      (v) => v.name.toLowerCase().includes(s) || (v.label ?? "").toLowerCase().includes(s)
    );
  }

  return (
    <div>
      <input
        placeholder={placeholder ?? "Buscar variable…"}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="input mb-2"
      />
      {onSetSelected && (
        <div className="mb-2">
          <AccionesSeleccion
            visibles={entries.map((v) => v.name)}
            seleccionados={selected}
            onCambiar={onSetSelected}
            nombre="variable(s)"
            filtrando={q.trim().length > 0}
          />
        </div>
      )}
      {sinFiltroDeClase && (
        <p className="mb-2 text-xs text-tinta-3">
          El diccionario no tiene ninguna variable {filterKind === "numeric" ? "numérica" : "categórica"}:
          se enseñan todas.
        </p>
      )}
      <div className="max-h-56 overflow-y-auto bg-white border border-grid rounded-md p-2">
        {dictQ.isLoading && <div className="text-xs text-tinta-3">Cargando diccionario…</div>}
        {entries.length === 0 && !dictQ.isLoading && (
          <div className="text-xs text-tinta-3">Sin variables.</div>
        )}
        {entries.map((v) => {
          const checked = selected.includes(v.name);
          return (
            <label
              key={v.name}
              className="flex items-start gap-2 py-1 cursor-pointer hover:bg-plano rounded px-1"
              title={v.label ?? v.name}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(v.name)}
                className="mt-0.5 accent-marca"
              />
              <span className="text-sm leading-tight">
                <span className="block text-tinta">{v.label || v.name}</span>
                <span className="block text-[10px] text-tinta-3 font-mono">
                  {v.name} · {v.type}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {!onSetSelected && selected.length > 0 && (
        <div className="mt-2 text-xs text-tinta-3">{selected.length} seleccionada(s)</div>
      )}
    </div>
  );
}
